import { Typography, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
  CUSTOMER_APPROVED,
  IN_PROGRESS,
  INCOMLEND_INTERNAL,
  INVOICE_CANCELLED,
  PENDING_CUSTOMER_APPROVAL,
  PENDING_APPROVAL_HOS,
  CUSTOMER_REJECTED,
  REJECTED_BY_HOS,
  TERMSHEET_CANCELLED,
} from "codes";
import { PERMISSIONS } from "permissions";
import type { CSSProperties } from "react";
import { useAppSelector } from "redux/hooks";

export type TermSheetViewingRole = "HOD" | "RM" | "CUSTOMER";

export const roleBasedBg = (
  role: TermSheetViewingRole,
  status: string,
  theme: Theme
) => {
  switch (status) {
    case REJECTED_BY_HOS:
      return role === "RM" ? "orange" : "purple";
    case PENDING_APPROVAL_HOS:
      return role === "RM" ? "purple" : "orange";
    case PENDING_CUSTOMER_APPROVAL:
      return role === "CUSTOMER" ? "orange" : "purple";
    case IN_PROGRESS:
      return "orange";
    case "REJECTED":
    case CUSTOMER_REJECTED:
      return theme.palette.error.main;
    case TERMSHEET_CANCELLED:
      return theme.palette.error.main;
    case INVOICE_CANCELLED:
      return theme.palette.error.main;
    case CUSTOMER_APPROVED:
      return theme.palette.success.main;
    default:
      return "";
  }
};

const StatusDisplay = ({
  status,
  style,
}: {
  status: string;
  style?: CSSProperties;
}) => {
  const theme = useTheme();
  const APP_PERMISSION = useAppSelector((state) => state.appState.permission);
  const isInternal = APP_PERMISSION === INCOMLEND_INTERNAL;
  const USER_PERMISSIONS = useAppSelector(
    (state) => state.appState.user_permission
  );
  const internalRole: TermSheetViewingRole = USER_PERMISSIONS.includes(
    PERMISSIONS.termsheet_approver
  )
    ? "HOD"
    : "RM";
  const currentRole: TermSheetViewingRole = isInternal
    ? internalRole
    : "CUSTOMER";

  const FINAL_STATUS: Record<string, { value: string; background: string; }> = {
    [REJECTED_BY_HOS]: {
      value: "Rejected by HOS",
      background: roleBasedBg(currentRole, REJECTED_BY_HOS, theme),
    },
    [PENDING_APPROVAL_HOS]: {
      value: "Pending Approval HOS",
      background: roleBasedBg(currentRole, PENDING_APPROVAL_HOS, theme),
    },
    [PENDING_CUSTOMER_APPROVAL]: {
      value: isInternal ? "Pending Customer Approval" : "Pending Approval",
      background: roleBasedBg(currentRole, PENDING_CUSTOMER_APPROVAL, theme),
    },
    [IN_PROGRESS]: {
      value: "In Progress",
      background: roleBasedBg(currentRole, IN_PROGRESS, theme),
    },
    [CUSTOMER_APPROVED]: {
      value: isInternal ? "Customer Approved" : "Approved",
      background: roleBasedBg(currentRole, CUSTOMER_APPROVED, theme),
    },
    REJECTED: {
      value: "Rejected",
      background: roleBasedBg(currentRole, "REJECTED", theme),
    },
    [CUSTOMER_REJECTED]: {
      value: isInternal ? "Customer Rejected" : "Rejected",
      background: roleBasedBg(currentRole, CUSTOMER_REJECTED, theme),
    },
    [TERMSHEET_CANCELLED]: {
      value: "Termsheet expired",
      background: roleBasedBg(currentRole, TERMSHEET_CANCELLED, theme)
    },
    [INVOICE_CANCELLED]: {
      value: "Invoice Cancelled",
      background: roleBasedBg(currentRole, INVOICE_CANCELLED, theme)
    }
  };

  return (
    <Typography
      style={{
        background: FINAL_STATUS[status]?.background,
        padding: "6px 16px",
        borderRadius: theme.shape.borderRadius * 2.5,
        color: "white",
        fontWeight: "600",
        textAlign: "center",
        width: "100%",
        cursor: "not-allowed",
        fontSize: "0.8em",
        ...style,
      }}
      sx={{
        [theme.breakpoints.down("sm")]: {
          fontSize: "1em",
        },
      }}
    >
      {FINAL_STATUS[status]?.value}
    </Typography>
  );
};
export default StatusDisplay;
